import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ClipboardCheck, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasAnyPermission, hasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';

import FacilityDispatchPage from './facility/FacilityDispatchPage';
import PlatformInboxPage from './platform/PlatformInboxPage';
import SupplierRequestsPage from './supplier/SupplierRequestsPage';

const NS = 'operations';

type DispatchLane = 'supplier' | 'platform' | 'facility';

interface LaneConfig {
  id: DispatchLane;
  label: string;
  description: string;
  icon: typeof Send;
}

export const DispatchWorkspacePage = () => {
  const { t } = useTranslation(NS);
  const location = useLocation();
  const user = useCurrentUser();

  const lanes = useMemo<LaneConfig[]>(() => {
    const out: LaneConfig[] = [];
    if (
      hasAnyPermission(user, [
        'dispatch.request.create',
        'dispatch.request.update_draft',
        'dispatch.request.submit',
        'dispatch.request.cancel',
      ])
    ) {
      out.push({
        id: 'supplier',
        label: t('efs.operations.dispatch.lanes.supplier', { defaultValue: '申請作成' }),
        description: t('efs.operations.dispatch.lanes.supplier_desc', {
          defaultValue: '供給元または代理で配車・配送依頼を作成',
        }),
        icon: Send,
      });
    }
    if (hasAnyPermission(user, ['dispatch.request.route', 'dispatch.request.reject_platform'])) {
      out.push({
        id: 'platform',
        label: t('efs.operations.dispatch.lanes.platform', { defaultValue: '平台確認' }),
        description: t('efs.operations.dispatch.lanes.platform_desc', {
          defaultValue: '確認申請並指派處理作業據點',
        }),
        icon: ClipboardCheck,
      });
    }
    if (
      hasAnyPermission(user, [
        'dispatch.request.accept',
        'dispatch.request.reject_facility',
        'dispatch.request.fulfill',
      ])
    ) {
      out.push({
        id: 'facility',
        label: t('efs.operations.dispatch.lanes.facility', { defaultValue: '據點作業' }),
        description: t('efs.operations.dispatch.lanes.facility_desc', {
          defaultValue: '接受、拒絕或完成入荷連結',
        }),
        icon: Building2,
      });
    }
    return out;
  }, [t, user]);

  const partyDefaultLane: DispatchLane =
    user?.partyType === 'platform'
      ? 'platform'
      : user?.partyType === 'processor'
        ? 'facility'
        : 'supplier';
  const initialLane: DispatchLane = location.pathname.startsWith('/platform')
    ? 'platform'
    : location.pathname.startsWith('/operations') || location.pathname.startsWith('/batches')
      ? 'facility'
      : partyDefaultLane;
  const firstAllowed = lanes.find((lane) => lane.id === initialLane)?.id ?? lanes[0]?.id;
  const [activeLane, setActiveLane] = useState<DispatchLane | undefined>(firstAllowed);
  const currentLane = lanes.find((lane) => lane.id === activeLane) ?? lanes[0];

  if (!hasPermission(user, 'dispatch.request.read') || lanes.length === 0) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t('efs.operations.dispatch.title')}
          subtitle={t('efs.operations.dispatch.no_permission', {
            defaultValue: '現在のロールには配車・配送フローの権限がありません',
          })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.operations.dispatch.title')}
        subtitle={t('efs.operations.dispatch.workflow_subtitle', {
          defaultValue: '同一張單從草稿、送出、平台確認、據點作業到完成入荷',
        })}
      />

      {lanes.length > 1 ? (
        <div className="grid gap-2 md:grid-cols-3">
          {lanes.map((lane) => (
            <Button
              key={lane.id}
              type="button"
              variant={currentLane?.id === lane.id ? 'default' : 'outline'}
              className={cn(
                'h-auto justify-start gap-3 px-3 py-3 text-left',
                currentLane?.id !== lane.id && 'bg-efs-bg-panel',
              )}
              onClick={() => setActiveLane(lane.id)}
            >
              <lane.icon className="h-4 w-4 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{lane.label}</span>
                <span className="block truncate text-xs opacity-75">{lane.description}</span>
              </span>
            </Button>
          ))}
        </div>
      ) : null}

      {currentLane?.id === 'supplier' ? <SupplierRequestsPage embedded /> : null}
      {currentLane?.id === 'platform' ? <PlatformInboxPage embedded /> : null}
      {currentLane?.id === 'facility' ? <FacilityDispatchPage embedded /> : null}
    </div>
  );
};

export default DispatchWorkspacePage;
